import { put, takeEvery, all, call, throttle, select, delay, race, take, debounce } from 'redux-saga/effects'
import { fromJS, List } from 'immutable'
import moment from 'moment-timezone'

import * as Api from 'api/content_desk'
import * as ContactsManagementApi from 'api/contacts_management'
import * as BffApi from 'api/bff'
import * as Selectors from 'selectors'
import * as Actions from 'actions/content_desk'
import * as AppActions from 'actions/app'
import * as NewsActions from 'actions/news'
import * as SavedSearchesActions from 'actions/saved_searches'
import * as NavigationActions from 'actions/navigation'
import * as FilterActions from 'actions/filter'
import { navigate } from 'actions/navigation'

import * as LocalStorage from 'utils/localStorage'
import { removeInvalidTagsForOts } from 'utils/lexicalEditorHelper'
import { downloadBlob } from 'utils/sagas'
import { listUniqueById } from 'utils/immutable'
import { ExcelMimeType } from 'static/constants'
import { otsBody as initialOtsBody } from 'static/ots_body_config'

const getLockedContentFromError = error => {
  if (error.response?.statusCode === 423 && error.response.message?.code === 'content_locked') {
    return error.response.message.content
  }

  return null
}

export function* getContentStart({ payload: { id } }) {
  try {
    const result = yield call(Api.getContent, id)
    yield put(Actions.getContentSuccess(result))
    yield put(Actions.fetchStatisticsStart({}))
  } catch (error) {
    yield put(Actions.getContentError(error))
    yield put(Actions.closeViewContentDialog())
    yield put(AppActions.showAppMessage({
      success: false
    }))
  }
}

export function* saveCampaignStart() {
  const i18n = yield select(Selectors.getI18n)

  try {
    const formData = yield select(Selectors.getCDeskCampaignFormData)
    const requestBody = {
      assigned_user_id: formData.get('assignedUserId'),
      end_date: formData.get('endDate'),
      id: formData.get('id'),
      name: formData.get('name'),
      start_date: formData.get('startDate'),
      summary: formData.get('summary'),
      active: formData.get('active')
    }

    let result
    let text

    if (formData.get('id')) {
      result = yield call(Api.updateCampaign, formData.get('id'), requestBody)
      text = i18n.get('ccd_campaign_updated')
    } else {
      result = yield call(Api.createCampaign, requestBody)
      text = i18n.get('ccd_campaign_created')
    }

    yield put(Actions.saveCampaignSuccess(result))

    const calendarDate = yield select(Selectors.getCDeskCalendarDate)
    yield put(Actions.fetchCalendarEventsStart(calendarDate))

    yield put(Actions.fetchCampaignDropdownOptionsStart())

    yield put(AppActions.showAppSnackbarMessage({
      text,
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.saveCampaignError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* saveContentFormlessStart({
  payload: {
    id,
    title,
    plannedFor,
    scheduledFor,
    scheduledForJobName,
    scheduledEventsJobName,
    approvalStatus,
    ccdCampaignId
  }
}) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const requestBody = {
      id,
      title,
      planned_for: plannedFor,
      scheduled_for: scheduledFor,
      scheduled_for_job_name: scheduledForJobName,
      scheduled_events_job_name: scheduledEventsJobName,
      approval_status: approvalStatus,
      ccd_campaign_id: ccdCampaignId
    }
    const result = yield call(Api.updateContent, id, requestBody)
    yield put(Actions.saveContentFormlessSuccess(result))
  } catch (error) {
    let msg = i18n.get('failed')
    const lockedContent = getLockedContentFromError(error)

    if (lockedContent) {
      msg = i18n.get('content_being_edited')
    }

    if (error.response && error.response.statusCode === 409) {
      msg = i18n.get('ccd_content_duplicate_error')
    }

    yield put(Actions.saveContentFormlessError(error))

    if (lockedContent) {
      const duplicateSource = fromJS(lockedContent)

      yield put(Actions.lockContentSuccess(duplicateSource))
      yield put(Actions.openBlockedContentEditDialog({ duplicateSource }))
    }

    yield put(AppActions.showAppSnackbarMessage({
      text: msg,
      variant: 'error'
    }))
  }
}

export function* saveContentStart({ payload }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const formData = yield select(Selectors.getCDeskContentFormData)
    const timezone = yield select(Selectors.getTimezone)
    const config = yield select(Selectors.getCDeskConfig)
    const ccdCampaign = formData.get('ccdCampaign')
    const type = formData.getIn(['type', 'id'])

    let ccdCampaignId = null

    if (ccdCampaign !== null) {
      ccdCampaignId = ccdCampaign.get('value')
    }

    const recipients = formData.get('recipients').map(r => ({
      content_id: r.get('contentId'),
      first_name: r.get('firstName'),
      last_name: r.get('lastName'),
      mail: r.get('mail'),
      contact_id: r.get('contactId'),
      distribution_list_id: r.get('distributionListId')
    }))

    const ccdMediaFiles = formData.get('ccdMediaFiles').map(f => ({
      id: f.get('id')
    }))

    const notes = formData.get('notes').map(n => ({
      text: n.get('text'),
      created_by: n.get('createdBy'),
      created_at: n.get('createdAt')
    }))

    const senderName = formData.get('senderName') || config.get('senderName')
    const senderMail = formData.get('senderMail') || config.get('senderEmail')
    const id = formData.get('id')

    const requestBody = {
      id,
      title: formData.get('title'),
      body: formData.get('body'),
      plain_text: formData.get('plainText'),
      editor_design: JSON.stringify(formData.get('editorDesign')),
      type,
      subject: formData.get('subject'),
      planned_for: formData.get('plannedFor'),
      scheduled_for: formData.get('scheduledFor'),
      ccd_campaign_id: ccdCampaignId,
      ccd_media_files: ccdMediaFiles,
      recipients,
      timezone,
      sender_mail: senderMail,
      sender_name: senderName,
      domain: formData.get('domain'),
      domain_region: formData.get('domainRegion'),
      approval_status: formData.get('approvalStatus'),
      notes
    }

    if (type === 'linkedin') {
      requestBody.linked_in_post = JSON.stringify(formData.get('linkedInPost'))
      requestBody.sender_mail = ''
      requestBody.sender_name = ''
      requestBody.domain = ''
      requestBody.domain_region = ''
    }

    if (type === 'facebook') {
      requestBody.facebook_post = JSON.stringify(formData.get('facebookPost'))
      requestBody.sender_mail = ''
      requestBody.sender_name = ''
      requestBody.domain = ''
      requestBody.domain_region = ''
    }

    if (type === 'ots_press_release') {
      requestBody.sender_mail = ''
      requestBody.sender_name = ''
      requestBody.domain = ''
      requestBody.domain_region = ''

      if (requestBody.body) {
        const parsedBody = JSON.parse(requestBody.body)
        let { text } = parsedBody
        let { contact } = parsedBody
        text = yield call(removeInvalidTagsForOts, text, ['P', 'I', 'B', 'U', 'A', 'UL', 'OL', 'LI'])
        contact = yield call(removeInvalidTagsForOts, contact, ['P', 'I', 'B', 'U', 'A', 'BR'])

        const plainStoryText = text.replace(/<\/?p[^>]*>/g, '\n').replace(/<[^>]*>/g, '')
        const plainContactText = contact.replace(/<\/?p[^>]*>/g, '\n').replace(/<[^>]*>/g, '')

        requestBody.plain_text = `${plainStoryText} ${plainContactText}`
        requestBody.body = JSON.stringify({ ...parsedBody, text, contact })
      }
    }

    let result
    let text

    if (id) {
      result = yield call(Api.updateContent, id, requestBody)
      text = i18n.get('ccd_content_updated')
    } else {
      result = yield call(Api.createContent, requestBody)
      text = i18n.get('ccd_content_created')
    }

    if (payload && payload.switchToContactManagement) {
      yield put(Actions.switchToContactManagement())
    }

    if (payload && payload.openEditContentDialog) {
      yield put(Actions.lockContentStart({ id: result.id, openEditContentDialog: true }))
    }

    if (payload && payload.openOldEditContentDialog) {
      yield put(Actions.lockContentStart({ id: result.id, openOldEditContentDialog: true }))
    }

    if (payload && payload.closeEditContentDialog) {
      yield put(Actions.unlockContentStart({ closeEditContentDialog: true }))
    }

    if (payload && payload.closeOldEditContentDialog) {
      yield put(Actions.unlockContentStart({ closeOldEditContentDialog: true }))
    }

    const calendarDate = yield select(Selectors.getCDeskCalendarDate)
    yield put(Actions.fetchCalendarEventsStart(calendarDate))

    yield put(Actions.saveContentSuccess(result))
    yield put(AppActions.showAppSnackbarMessage({
      text,
      variant: 'success'
    }))
  } catch (error) {
    let msg = i18n.get('failed')
    const lockedContent = getLockedContentFromError(error)

    if (lockedContent) {
      msg = i18n.get('content_being_edited')
    }

    if (error.response && error.response.statusCode === 409) {
      msg = i18n.get('ccd_content_duplicate_error')
    }

    if (error.response && error.response.statusCode === 403) {
      msg = error.response.message
    }

    if (error.response && error.response.statusCode === 400) {
      const responseMessage = error.response.message

      if (typeof responseMessage === 'string') {
        msg = responseMessage
      } else if (responseMessage && typeof responseMessage === 'object' && typeof responseMessage.text === 'string') {
        msg = responseMessage.text
      }
    }

    yield put(Actions.setContentRecipientIssues(error.response?.message?.invalidRecipients || []))

    if (lockedContent) {
      const duplicateSource = yield select(Selectors.getCDeskContentFormData)

      yield put(Actions.lockContentSuccess(fromJS(lockedContent)))
      yield put(Actions.openBlockedContentEditDialog({ duplicateSource }))
    }

    yield put(Actions.saveContentError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: msg,
      variant: 'error'
    }))
  }
}

export function* deleteContentStart({ payload }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const result = yield call(Api.deleteContent, payload.id)
    yield put(Actions.deleteContentSuccess(result))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('content_deleted'),
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.deleteContentError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* deleteCampaignStart({ payload }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const result = yield call(Api.deleteCampaign, payload.id)
    yield put(Actions.deleteCampaignSuccess(result))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('ccd_campaign_deleted'),
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.deleteCampaignError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* cloneContentsFormData() {
  const uploadedMediaFiles = yield select(Selectors.getCDeskUploadedMediaFiles)

  yield put(Actions.fetchCampaignDropdownOptionsStart())

  if (uploadedMediaFiles.isEmpty()) {
    yield put(Actions.fetchMediaFilesStart())
  }
}
export function* duplicateLockedContent({ payload: content }) {
  const contentDeskContentTypes = yield select(Selectors.getStaticCdContentTypes)
  const contentType = content.getIn(['type', 'id']) || content.get('type')
  const currentType = contentDeskContentTypes.find(type => type.get('id') === contentType)
  const campaign = content.get('ccdCampaign')
  const campaignId = campaign && (campaign.get('value') || campaign.get('id'))
  const campaignName = campaign && (campaign.get('label') || campaign.get('name'))
  const mediaFiles = content.get('ccdMediaFiles', List())
  let linkedInPost

  if (contentType === 'linkedin' && content.get('linkedInPost')) {
    linkedInPost = content.get('linkedInPost').delete('createdPostId')

    if (mediaFiles.size === 1) {
      linkedInPost = linkedInPost.setIn(['content', 'media', 'id'], String(mediaFiles.first().get('id')))
    }

    if (mediaFiles.size > 1) {
      let linkedInImages = mediaFiles.map(selected => fromJS({
        altText: selected.get('filename'),
        id: String(selected.get('id'))
      }))
      linkedInImages = listUniqueById(linkedInImages)
      linkedInPost = linkedInPost.set('content', fromJS({
        multiImage: {
          images: linkedInImages
        }
      }))
    }
  }

  let facebookPost

  if (contentType === 'facebook' && content.get('facebookPost')) {
    facebookPost = content.get('facebookPost').delete('createdPostId')
    const mediaAttachments = facebookPost
      .get('mediaAttachments', List())
      .map(attachment => attachment.delete('fbid'))
    facebookPost = facebookPost.set('mediaAttachments', mediaAttachments)
  }

  const typeOption = currentType ? {
    id: currentType.get('id'),
    value: currentType.get('id'),
    label: currentType.get('name')
  } : { id: contentType, value: contentType, label: contentType }
  const duplicate = content
    .set('ccdCampaign', campaign ? fromJS({ value: campaignId, label: campaignName }) : null)
    .set('type', fromJS(typeOption))
    .set('id', null)
    .set('scheduledFor', null)
    .set('recipients', List())
    .set('linkedInPost', linkedInPost)
    .set('facebookPost', facebookPost)
    .set('title', `${content.get('title')} - duplicate(1)`)
    .set('scheduledJobName', null)
    .set('scheduledEventsJobName', null)
    .set('status', null)
    .set('lockedAt', null)
    .set('lockedBy', null)
  yield put(Actions.cloneContentsFormData(duplicate))
  yield put(Actions.closeBlockedContentEditDialog())
  yield put(Actions.saveContentStart({ openEditContentDialog: true }))
}

export function* openViewContentDialog({ payload: selectedContent }) {
  yield put(Actions.getContentStart({ id: selectedContent.get('id') }))
}

export function* openEditContentDialog() {
  const uploadedMediaFiles = yield select(Selectors.getCDeskUploadedMediaFiles)

  yield put(Actions.fetchCampaignDropdownOptionsStart())

  if (uploadedMediaFiles.isEmpty()) {
    yield put(Actions.fetchMediaFilesStart())
  }
}

export function* openOldEditContentDialog() {
  const uploadedMediaFiles = yield select(Selectors.getCDeskUploadedMediaFiles)

  yield put(Actions.fetchCampaignDropdownOptionsStart())

  if (uploadedMediaFiles.isEmpty()) {
    yield put(Actions.fetchMediaFilesStart())
  }
}

export function* fetchCampaignDropdownOptionsStart() {
  const i18n = yield select(Selectors.getI18n)

  try {
    const body = {
      select_fields: ['id', 'name', 'assigned_user_id', 'active', 'start_date', 'end_date', 'summary'],
      page: 1,
      limit: 1000
    }

    const result = yield call(Api.searchCampaigns, body)
    yield put(Actions.fetchCampaignDropdownOptionsSuccess(result))
  } catch (error) {
    yield put(Actions.fetchCampaignDropdownOptionsError(error))
    yield put(AppActions.showAppMessage({
      text: i18n.get('failed'),
      success: false
    }))
  }
}

export function* fetchContentDropdownOptionsStart({ payload: { title, type } }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const body = {
      filter: [{ field: 'title', value: title || '' }, { field: 'type', value: type || 'email' }],
      page: 1,
      limit: 10
    }

    const result = yield call(Api.searchContents, body)
    yield put(Actions.fetchContentDropdownOptionsSuccess(result))
  } catch (error) {
    yield put(Actions.fetchContentDropdownOptionsError(error))
    yield put(AppActions.showAppMessage({
      text: i18n.get('failed'),
      success: false
    }))
  }
}

export function* fetchMediaFilesStart() {
  const i18n = yield select(Selectors.getI18n)

  try {
    const body = {
      page: 1,
      limit: 10
    }

    const result = yield call(Api.searchMediaFiles, body)
    yield put(Actions.fetchMediaFilesSuccess(result))
    yield put(Actions.setMediaFilesInitialFetch(true))
    yield put(Actions.incrementMediaFilesPage())
  } catch (error) {
    yield put(Actions.fetchMediaFilesError(error))
    yield put(AppActions.showAppMessage({
      text: i18n.get('failed'),
      success: false
    }))
  }
}

export function* fetchMoreMediaFilesStart({ payload }) {
  const i18n = yield select(Selectors.getI18n)
  const isInitialFetch = yield select(Selectors.getCDeskMediaFilesInitialFetch)
  const uploadedMediaFiles = yield select(Selectors.getCDeskUploadedMediaFiles)
  const uploadedMediaFilesTotal = yield select(Selectors.getCDeskUploadedMediaFilesTotal)
  const page = yield select(Selectors.getCDeskMediaFilesPage)
  const limit = yield select(Selectors.getCDeskMediaFilesLimit)

  const hasMore = uploadedMediaFilesTotal > page * limit
  const mediaFiles = uploadedMediaFiles
    .filter(mediaFile => mediaFile.get('mimeType') !== null)
    .filter(mediaFile => mediaFile.get('mimeType').includes('image'))
    .map(mediaFile => ({
      id: mediaFile.get('id'),
      location: mediaFile.get('url'),
      contentType: mediaFile.get('mimeType'),
      width: 500,
      height: 500,
      source: 'user'
    })).toJS()

  try {
    const body = {
      page,
      limit
    }

    if (isInitialFetch && payload.done) {
      yield put(Actions.setMediaFilesInitialFetch(false))
      yield call(payload.done, mediaFiles, { hasMore, page, limit, uploadedMediaFilesTotal })
      yield put(Actions.fetchMoreMediaFilesSuccess())

      return
    }

    const result = yield call(Api.searchMediaFiles, body)

    if (payload.done) {
      const newMediaFiles = mediaFiles.concat(result.mediaFiles
        .filter(mediaFile => mediaFile.mimeType.includes('image'))
        .map(mediaFile => ({
          id: mediaFile.id,
          location: mediaFile.url,
          contentType: mediaFile.mimeType,
          width: 500,
          height: 500,
          source: 'user'
        })))

      yield call(payload.done, newMediaFiles, { hasMore, page, limit, uploadedMediaFilesTotal })
    }

    yield put(Actions.appendMediaFiles(result))
    yield put(Actions.incrementMediaFilesPage())

    yield put(Actions.fetchMoreMediaFilesSuccess())
  } catch (error) {
    yield put(Actions.fetchMoreMediaFilesError(error))
    yield put(AppActions.showAppMessage({
      text: i18n.get('failed'),
      success: false
    }))
  }
}

export function* deleteMediaFileStart({ payload }) {
  const uploadedMediaFiles = yield select(Selectors.getCDeskUploadedMediaFiles)
  const storedFileId = uploadedMediaFiles.find(mediaFile => mediaFile.get('id') === payload.id).get('storedFilesId')

  try {
    const { ccdMediaFile, total, totalSize } = yield call(Api.deleteMediaFile, storedFileId)
    yield put(Actions.deleteMediaFileSuccess({ ccdMediaFile, total }))
    yield put(Actions.setUploadedMediaFilesTotalSize(totalSize))
  } catch (_error) {
    yield put(Actions.deleteMediaFileError())
  }
}

export function* fetchDistributionListDropdownOptionsStart() {
  const i18n = yield select(Selectors.getI18n)

  try {
    const body = {
      filter: [{ field: 'name', value: '' }],
      unsubscribed_contacts: false,
      page: 1,
      limit: 1000
    }

    const result = yield call(ContactsManagementApi.fetchDistributionLists, body)
    yield put(Actions.fetchDistributionListDropdownOptionsSuccess(result))
  } catch (error) {
    yield put(Actions.fetchDistributionListDropdownOptionsError(error))
    yield put(AppActions.showAppMessage({
      text: i18n.get('failed'),
      success: false
    }))
  }
}

export function* fetchContactDropdownOptionsStart({ payload: { searchString } }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const body = {
      filter: [{ field: 'searchable', value: searchString }],
      unsubscribed: false,
      page: 1,
      limit: 20
    }

    const result = yield call(ContactsManagementApi.fetchContacts, body)
    yield put(Actions.fetchContactDropdownOptionsSuccess(result))
  } catch (error) {
    yield put(Actions.fetchContactDropdownOptionsError(error))
    yield put(AppActions.showAppMessage({
      text: i18n.get('failed'),
      success: false
    }))
  }
}

export function* uploadEditorImageStart({ payload: { file } }) {
  try {
    /* eslint-disable camelcase */
    const { uploaded_file, ccd_media_files_total_size } = yield call(Api.uploadEditorImage, file)
    yield put(Actions.uploadEditorImageSuccess(uploaded_file))
    yield put(Actions.setUploadedMediaFilesTotalSize(ccd_media_files_total_size))
  } catch (_error) {
    yield put(Actions.uploadEditorImageError())
  }
}

export function* fetchStatisticsStart({ payload }) {
  const content = yield select(Selectors.getCDeskSelectedContent)

  if (content.get('status') !== 'released') {
    return
  }

  const i18n = yield select(Selectors.getI18n)
  const id = content.get('id')
  const earnedMediaAccuracy = payload.earnedMediaAccuracy || '30%'

  try {
    const result = yield call(Api.fetchStatistics, id, earnedMediaAccuracy)
    yield put(Actions.fetchStatisticsSuccess(result))
  } catch (error) {
    // when post cannot be found on social media because it has been deleted there
    // then update the content
    if (error && error.response && error.response.statusCode === 404) {
      if (!content.get('deletedExternally')) {
        yield put(Actions.saveDeletedExternallyForContentStart({ content }))
      }
    } else {
      yield put(AppActions.showAppMessage({
        text: i18n.get('failed'),
        success: false
      }))
    }

    yield put(Actions.fetchStatisticsError(error))
  }
}

export function* saveDeletedExternallyForContentStart({ payload: { content } }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const id = content.get('id')

    // Minimum required fields for patch although we only want to update deleted_externally
    const requestBody = {
      title: content.get('title'),
      planned_for: content.get('plannedFor'),
      scheduled_for: content.get('scheduledFor'),
      scheduled_for_job_name: content.get('scheduledForJobName'),
      scheduled_events_job_name: content.get('scheduledEventsJobName'),
      ccd_campaign_id: content.getIn(['ccdCampaign', 'id']),
      ccd_media_files: content.get('ccdMediaFiles').map(f => ({ id: f.get('id') })),
      deleted_externally: true
    }

    const result = yield call(Api.updateContent, id, requestBody)
    yield put(Actions.saveDeletedExternallyForContentSuccess(result))
  } catch (error) {
    yield put(Actions.saveDeletedExternallyForContentError(error))
    yield put(AppActions.showAppMessage({
      text: i18n.get('failed'),
      success: false
    }))
  }
}

export function* lockContent({ payload }) {
  const { id, takeOver = false } = payload || {}
  const lockOnly = (!payload
    || (payload && (!payload.openContentNotesDialog && (!payload.openEditContentDialog && !payload.openOldEditContentDialog)))
  )

  const i18n = yield select(Selectors.getI18n)
  const contentDeskContentTypes = yield select(Selectors.getStaticCdContentTypes)

  try {
    let contentId

    if (!id && lockOnly) {
      const content = yield select(Selectors.getCDeskSelectedContent)
      contentId = content.get('id')
    } else {
      contentId = id
    }

    if (contentId) {
      const result = takeOver
        ? yield call(Api.lockContent, contentId, { take_over: true })
        : yield call(Api.lockContent, contentId)
      const user = yield select(Selectors.getUser)

      yield put(Actions.lockContentSuccess(fromJS(result)))

      if (!lockOnly) {
        const lockedByCurrentUser = result.lockedBy === user.get('id')

        if (lockedByCurrentUser) {
          const ccdCampaign = { value: result.ccdCampaign.id, label: result.ccdCampaign.name }
          const filteredType = contentDeskContentTypes.filter(t => t.get('id') === result.type).first()

          let type = { value: contentDeskContentTypes.first().get('id'), label: contentDeskContentTypes.first().get('name') }

          if (filteredType) {
            type = { id: filteredType.get('id'), value: filteredType.get('id'), label: filteredType.get('name') }
          }

          const lockedContent = fromJS({
            ...result,
            ...{ ccdCampaign },
            ...{ type }
          })

          if (payload && payload.openContentNotesDialog) {
            yield put(Actions.openContentNotesDialog(lockedContent))
          }

          if (payload && payload.openEditContentDialog) {
            yield put(Actions.changeContentEditTabView(type.id === 'email' ? 'settings' : 'choose_content'))
            yield put(Actions.openEditContentDialog(lockedContent))
          }

          if (payload && payload.openOldEditContentDialog) {
            yield put(Actions.openOldEditContentDialog(lockedContent))
          }
        } else {
          yield put(Actions.openBlockedContentEditDialog({
            editIntent: {
              openContentNotesDialog: payload?.openContentNotesDialog,
              openEditContentDialog: payload?.openEditContentDialog,
              openOldEditContentDialog: payload?.openOldEditContentDialog
            },
            duplicateSource: fromJS(result)
          }))
        }
      }
    }
  } catch (error) {
    yield put(Actions.lockContentError(error))
    yield put(AppActions.showAppMessage({
      text: i18n.get('failed'),
      success: false
    }))
  }
}

export function* unlockContent({ payload }) {
  const unlockOnly = (
    payload && (!payload.closeEditContentDialog && !payload.closeContentNotesDialog && !payload.closeOldEditContentDialog)
  )
  const content = yield select(Selectors.getCDeskSelectedContent)
  const i18n = yield select(Selectors.getI18n)
  const contentId = content.get('id')

  try {
    if (contentId) {
      const result = yield call(Api.unlockContent, contentId)
      yield put(Actions.unlockContentSuccess(fromJS(result)))
    }
  } catch (error) {
    yield put(Actions.unlockContentError(error))
    yield put(AppActions.showAppMessage({
      text: i18n.get('failed'),
      success: false
    }))
  } finally {
    if (!unlockOnly) {
      if (payload && payload.closeEditContentDialog) {
        yield put(Actions.closeEditContentDialog())
      }

      if (payload && payload.closeOldEditContentDialog) {
        yield put(Actions.closeOldEditContentDialog())
      }

      if (payload && payload.closeContentNotesDialog) {
        yield put(Actions.closeContentNotesDialog())
      }
    }
  }
}

export function* dispatchContentRequestStart({ payload: { test = false, recipient } }) {
  const i18n = yield select(Selectors.getI18n)
  try {
    const formData = yield select(Selectors.getCDeskContentFormData)

    const requestBody = {
      test
    }

    if (recipient) {
      requestBody.recipient = recipient
    }

    yield call(Api.dispatchContent, formData.get('id'), requestBody)
    yield put(Actions.dispatchContentRequestSuccess())
    yield put(AppActions.showAppMessage({
      text: i18n.get('success'),
      success: true
    }))
  } catch (_error) {
    yield put(Actions.dispatchContentRequestError())
    yield put(AppActions.showAppMessage({
      text: i18n.get('failed'),
      success: false
    }))
  }
}

export function* switchToContactManagement() {
  yield put(Actions.closeEditContentDialog())
  yield put(Actions.closeOldEditContentDialog())
  yield put(Actions.closeCalendarDialog())
  yield put(navigate('/app/contact_management/contacts'))
}

export function* switchToSearchPool({ payload: { field, value } }) {
  try {
    yield put(NewsActions.blockNewsRequest())

    const isPlotlights = yield select(Selectors.isPlotlights)

    yield put(NavigationActions.navigate(isPlotlights ? '/app/media_monitoring' : '/app/search_pool'))

    const { timeout } = yield race({
      selected: take(NewsActions.viewConfigPresetSelected),
      timeout: delay(2000)
    })

    if (!timeout) {
      yield put(NewsActions.resetNews())
      yield put(FilterActions.resetFilters())

      yield put(FilterActions.setSimilarityFilter({
        field,
        value: fromJS([value.delete('title')])
      }))

      if (field === 'ccdContents' && value.get('releasedAt')) {
        const dateFrom = value.get('releasedAt')
        const dateTo = moment(dateFrom).add(30, 'days').format()

        yield put(FilterActions.changeDate({
          dateFrom,
          dateTo
        }))
      }

      yield put(SavedSearchesActions.setInitialSearchLoaded(true))

      yield put(NewsActions.unblockNewsRequest())
      yield put(NewsActions.newsRequestStart())
    }
  } catch (error) {
    yield put(AppActions.genericErrorMessage())
    yield put(AppActions.exception(error))
  } finally {
    yield put(NewsActions.unblockNewsRequest())
  }
}

export function* uploadLinkedInMediaFileStart({ payload: { file } }) {
  try {
    if (file) {
      const formData = [
        {
          key: 'file',
          value: file
        }
      ]

      /* eslint-disable camelcase */
      const { uploaded_file, ccd_media_files_total_size } = yield call(Api.uploadMediaFile, formData)
      yield put(Actions.uploadLinkedInMediaFileSuccess(uploaded_file))
      yield put(Actions.setUploadedMediaFilesTotalSize(ccd_media_files_total_size))
    }
  } catch (_error) {
    yield put(Actions.uploadLinkedInMediaFileError())
  }
}

export function* uploadFacebookMediaFileStart({ payload: { file } }) {
  try {
    if (file) {
      const formData = [
        {
          key: 'file',
          value: file
        }
      ]

      /* eslint-disable camelcase */
      const { uploaded_file, ccd_media_files_total_size } = yield call(Api.uploadMediaFile, formData)
      yield put(Actions.uploadFacebookMediaFileSuccess(uploaded_file))
      yield put(Actions.setUploadedMediaFilesTotalSize(ccd_media_files_total_size))
    }
  } catch (_error) {
    yield put(Actions.uploadFacebookMediaFileError())
  }
}

export function* editorAiRequestStart({ payload: { contentType, actionType, language, mood } }) {
  try {
    const sourceText = yield select(Selectors.getCDeskAiSelectedText)
    const currentContentData = yield select(Selectors.getCDeskContentFormData)
    const totalEntries = yield select(Selectors.getCDeskAiRequestResults)

    let currentText = ''
    let result

    if (contentType === 'facebook') {
      currentText = currentContentData.getIn(['facebookPost', 'message'])
    }

    if (contentType === 'linkedIn') {
      currentText = currentContentData.getIn(['linkedInPost', 'commentary'])
    }

    const body = {
      action_type: actionType,
      source_text: sourceText || currentText,
      language,
      mood
    }

    const generatedText = yield call(Api.editorAiRequest, body)

    if (mood) {
      result = {
        resultId: totalEntries.size + 1,
        action: actionType.toLowerCase(),
        text: generatedText,
        mood,
        source: sourceText || currentText
      }
    }

    if (!mood) {
      result = {
        resultId: totalEntries.size + 1,
        action: actionType.toLowerCase(),
        text: generatedText,
        source: sourceText || currentText
      }
    }

    yield put(Actions.editorAiRequestSuccess(result))
  } catch (error) {
    yield put(Actions.editorAiRequestError(error))
  }
}

export function* buildSocialMediaLinkStart({ payload }) {
  try {
    if (payload.socialMedia === 'linkedin') {
      const { title, url, image, host, description } = yield call(BffApi.fetchOpenGraphMetadata, { url: payload.url })

      let articleContent

      if (image) {
        const file = yield call(BffApi.uploadFileFromUrl, { url: image, keep_until: moment().add(30, 'days').format() })
        articleContent = { title, url, description, id: file.id, host, imageUrl: file.url }
      } else {
        articleContent = { title, url, description, host }
      }

      yield put(Actions.buildSocialMediaLinkLinkedInSuccess(articleContent))
    }

    if (payload.socialMedia === 'facebook') {
      const result = yield call(Api.fetchFacebookLinkMetadata, { url: payload.url })
      yield put(Actions.buildSocialMediaLinkFacebookSuccess(result))
    }
  } catch (error) {
    if (payload.socialMedia === 'linkedin') {
      yield put(Actions.buildSocialMediaLinkLinkedInError({ url: payload.url }))
    }

    if (payload.socialMedia === 'facebook') {
      yield put(Actions.buildSocialMediaLinkFacebookError({ url: payload.url }))
    }
  }
}

export function* downloadEmailEvents({ payload }) {
  const { events } = payload
  const i18n = yield select(Selectors.getI18n)

  try {
    const result = yield call(Api.downloadEmailEvents, { events })
    yield call(downloadBlob, result, 'recipients_events_log.xlsx', ExcelMimeType)
    yield put(Actions.downloadEmailEventsSuccess())
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('download_in_progress'),
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.downloadEmailEventsError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('download_failed'),
      variant: 'error'
    }))
  }
}

export function* saveCampaignFormlessStart({ payload }) {
  const { id } = payload
  const i18n = yield select(Selectors.getI18n)

  try {
    const requestBody = {
      id,
      assigned_user_id: payload.assignedUserId,
      active: payload.active,
      start_date: payload.startDate,
      end_date: payload.endDate,
      summary: payload.summary
    }

    const result = yield call(Api.updateCampaign, id, requestBody)

    yield put(Actions.saveCampaignFormlessSuccess(result))
  } catch (error) {
    yield put(Actions.saveCampaignFormlessError({ error, id }))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* openNewContent({ payload }) {
  const { type } = payload
  const i18n = yield select(Selectors.getI18n)
  const title = i18n.get('new_content_title', { type: i18n.get(type), timestamp: moment().format() })

  if (type === 'email') {
    const config = yield select(Selectors.getCDeskConfig)

    yield put(Actions.setContentFormData({
      key: 'domain',
      value: config.get('domain')
    }))

    yield put(Actions.setContentFormData({
      key: 'domainRegion',
      value: config.get('domainRegion')
    }))
  }

  if (type === 'ots_press_release') {
    const userId = yield select(Selectors.getUserId)
    const newsradarId = yield select(Selectors.getNewsradarId)
    const key = `ccd_ots_press_release_${newsradarId}_${userId}:show_welcome_message`
    const value = yield call(LocalStorage.getItem, key)

    if (value !== 'false') {
      yield put(Actions.openOtsWelcomeDialog())

      return
    }

    yield put(Actions.setContentFormData({
      key: 'body',
      value: JSON.stringify(initialOtsBody)
    }))
  }

  yield put(Actions.setContentFormData({
    key: 'title',
    value: title
  }))

  const tab = () => {
    if (type === 'ots_press_release') {
      return 'choose_content'
    }

    return 'settings'
  }

  yield put(Actions.changeContentEditTabView(tab()))

  if (type === 'email' || type === 'ots_press_release') {
    yield put(Actions.setContentFormData({
      key: 'type',
      value: { label: type, id: type }
    }))

    yield put(Actions.saveContentStart({ openEditContentDialog: true }))
  } else {
    // use old editor for social media content for now
    yield put(Actions.saveContentStart({ openOldEditContentDialog: true }))
  }
}

export function* closeOtsWelcomeDialog() {
  const userId = yield select(Selectors.getUserId)
  const newsradarId = yield select(Selectors.getNewsradarId)
  const key = `ccd_ots_press_release_${newsradarId}_${userId}:show_welcome_message`
  yield call(LocalStorage.setItem, key, 'false')
}

export function* fetchKpiStatistics({ payload }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const filters = yield select(Selectors.getCDeskKpiStatisticsFilters)
    const type = filters.get('type')
    const dateRange = filters.get('dateRange')
    const invalidateCache = payload?.invalidateCache || false

    let days = 30

    if (dateRange === 'last_7_days') {
      days = 7
    }

    if (dateRange === 'last_30_days') {
      days = 30
    }

    if (dateRange === 'last_3_months') {
      days = Math.abs(moment().subtract(3, 'months').diff(moment(), 'days'))
    }

    const result = yield call(Api.fetchContentDeskKpiStatistics, type, days, invalidateCache)
    yield put(Actions.fetchKpiStatisticsSuccess(result))
  } catch (error) {
    yield put(Actions.fetchKpiStatisticsError(error))
    yield put(AppActions.showAppMessage({
      text: i18n.get('failed'),
      success: false
    }))
  }
}

export function* createOtsDraft({ payload: { id } }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const result = yield call(Api.createOtsDraft, id)
    yield put(Actions.createOtsDraftSuccess(result))
  } catch (error) {
    let msg = i18n.get('failed')

    if (error.response && error.response.statusCode === 424) {
      msg = error.response.message
    }

    yield put(Actions.createOtsDraftError(error))
    yield put(AppActions.showAppMessage({
      text: msg,
      success: false
    }))
  }
}

export function* watchSaveCampaignStart() {
  yield takeEvery(Actions.saveCampaignStart, saveCampaignStart)
}

export function* watchSaveCampaignFormlessStart() {
  yield takeEvery(Actions.saveCampaignFormlessStart, saveCampaignFormlessStart)
}

export function* watchSaveContentFormlessStart() {
  yield takeEvery(Actions.saveContentFormlessStart, saveContentFormlessStart)
}

export function* watchSaveContentStart() {
  yield takeEvery(Actions.saveContentStart, saveContentStart)
}

export function* watchDeleteContentStart() {
  yield takeEvery(Actions.deleteContentStart, deleteContentStart)
}

export function* watchDeleteCampaignStart() {
  yield takeEvery(Actions.deleteCampaignStart, deleteCampaignStart)
}

export function* watchCloneContentsFormData() {
  yield takeEvery(Actions.cloneContentsFormData, cloneContentsFormData)
}

export function* watchDuplicateLockedContentStart() {
  yield takeEvery(Actions.duplicateLockedContentStart, duplicateLockedContent)
}

export function* watchOpenViewContentDialog() {
  yield takeEvery(Actions.openViewContentDialog, openViewContentDialog)
}

export function* watchOpenEditContentDialog() {
  yield takeEvery(Actions.openEditContentDialog, openEditContentDialog)
}

export function* watchOpenOldEditContentDialog() {
  yield takeEvery(Actions.openOldEditContentDialog, openOldEditContentDialog)
}

export function* watchFetchCampaignDropdownOptionsStart() {
  yield takeEvery(Actions.fetchCampaignDropdownOptionsStart, fetchCampaignDropdownOptionsStart)
}

export function* watchFetchContentDropdownOptionsStart() {
  yield takeEvery(Actions.fetchContentDropdownOptionsStart, fetchContentDropdownOptionsStart)
}

export function* watchFetchContactDropdownOptionsStart() {
  yield debounce(1000, Actions.fetchContactDropdownOptionsStart, fetchContactDropdownOptionsStart)
}

export function* watchFetchDistributionListDropdownOptionsStart() {
  yield throttle(1000, Actions.fetchDistributionListDropdownOptionsStart, fetchDistributionListDropdownOptionsStart)
}

export function* watchFetchMediaFilesStart() {
  yield throttle(1000, Actions.fetchMediaFilesStart, fetchMediaFilesStart)
}

export function* watchFetchMoreMediaFilesStart() {
  yield throttle(1000, Actions.fetchMoreMediaFilesStart, fetchMoreMediaFilesStart)
}

export function* watchUploadEditorImageStart() {
  yield takeEvery(Actions.uploadEditorImageStart, uploadEditorImageStart)
}

export function* watchDeleteMediaFileStart() {
  yield takeEvery(Actions.deleteMediaFileStart, deleteMediaFileStart)
}

export function* watchFetchStatisticsStart() {
  yield takeEvery(Actions.fetchStatisticsStart, fetchStatisticsStart)
}

export function* watchGetContentStart() {
  yield takeEvery(Actions.getContentStart, getContentStart)
}

export function* watchLockContentFormStart() {
  yield takeEvery(Actions.lockContentStart, lockContent)
}

export function* watchUnlockContentFormStart() {
  yield takeEvery(Actions.unlockContentStart, unlockContent)
}

export function* watchDispatchContentRequestStart() {
  yield takeEvery(Actions.dispatchContentRequestStart, dispatchContentRequestStart)
}

export function* watchSwitchToContactManagement() {
  yield takeEvery(Actions.switchToContactManagement, switchToContactManagement)
}

export function* watchSwitchToSearchPool() {
  yield takeEvery(Actions.switchToSearchPool, switchToSearchPool)
}

export function* watchUploadLinkedInMediaFileStart() {
  yield takeEvery(Actions.uploadLinkedInMediaFileStart, uploadLinkedInMediaFileStart)
}

export function* watchUploadFacebookMediaFileStart() {
  yield takeEvery(Actions.uploadFacebookMediaFileStart, uploadFacebookMediaFileStart)
}

export function* watchSaveDeletedExternallyForContentStart() {
  yield takeEvery(Actions.saveDeletedExternallyForContentStart, saveDeletedExternallyForContentStart)
}

export function* watchEditorAiRequestStart() {
  yield takeEvery(Actions.editorAiRequestStart, editorAiRequestStart)
}

export function* watchBuildSocialMediaLinkStart() {
  yield takeEvery(Actions.buildSocialMediaLinkStart, buildSocialMediaLinkStart)
}

export function* watchDownloadEmailEvents() {
  yield takeEvery(Actions.downloadEmailEventsStart, downloadEmailEvents)
}

export function* watchOpenNewContent() {
  yield takeEvery(Actions.openNewContent, openNewContent)
}

export function* watchCloseOtsWelcomeDialog() {
  yield takeEvery(Actions.closeOtsWelcomeDialog, closeOtsWelcomeDialog)
}

export function* watchFetchKpiStatistics() {
  yield takeEvery(Actions.fetchKpiStatisticsStart, fetchKpiStatistics)
}

export function* watchCreateOtsDraft() {
  yield takeEvery(Actions.createOtsDraftStart, createOtsDraft)
}

export default function* mainSaga() {
  yield all([
    watchSaveCampaignStart(),
    watchSaveCampaignFormlessStart(),
    watchSaveContentFormlessStart(),
    watchSaveContentStart(),
    watchDuplicateLockedContentStart(),
    watchDeleteContentStart(),
    watchDeleteCampaignStart(),
    watchCloneContentsFormData(),
    watchOpenEditContentDialog(),
    watchOpenOldEditContentDialog(),
    watchFetchCampaignDropdownOptionsStart(),
    watchFetchContentDropdownOptionsStart(),
    watchFetchContactDropdownOptionsStart(),
    watchFetchDistributionListDropdownOptionsStart(),
    watchFetchMediaFilesStart(),
    watchFetchMoreMediaFilesStart(),
    watchDeleteMediaFileStart(),
    watchUploadEditorImageStart(),
    watchFetchStatisticsStart(),
    watchLockContentFormStart(),
    watchUnlockContentFormStart(),
    watchOpenViewContentDialog(),
    watchDispatchContentRequestStart(),
    watchSwitchToContactManagement(),
    watchSwitchToSearchPool(),
    watchUploadLinkedInMediaFileStart(),
    watchUploadFacebookMediaFileStart(),
    watchSaveDeletedExternallyForContentStart(),
    watchEditorAiRequestStart(),
    watchBuildSocialMediaLinkStart(),
    watchDownloadEmailEvents(),
    watchOpenNewContent(),
    watchCloseOtsWelcomeDialog(),
    watchFetchKpiStatistics(),
    watchGetContentStart(),
    watchCreateOtsDraft()
  ])
}
